import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Logo, Icon } from '@lnr-v2/ui';
import { ThemeToggle } from '@lnr-v2/ui';
import { AdminBreadcrumb } from '../../components/AdminBreadcrumb';
import type { BreadcrumbItem } from '../../components/AdminBreadcrumb';
import { Avatar } from '../../components/atoms';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { DecorativeMenuButton } from '../../components/DecorativeMenuButton';
import { SlideNavigation } from '../../components/SlideNavigation';
import styles from './AdminLayout.module.css';

interface AdminLayoutProps {
  children: React.ReactNode;
}

// Define navigation items
const navigationItems = [
  { label: 'Dashboard', path: '/admin/dashboard' },
  { label: 'Users', path: '/admin/users' },
  { label: 'Clients', path: '/admin/clients' },
  { label: 'Cases', path: '/admin/cases' },
  { label: 'Layouts', path: '/admin/layouts' },
  { label: 'Tags & Categories', path: '/admin/taxonomies' },
  { label: 'Metrics', path: '/admin/metrics' },
  { label: 'Translations', path: '/admin/translations' },
  { label: 'Alert Banner', path: '/admin/alert-banner' },
  { label: 'Settings', path: '/admin/settings' },
];

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [navOpen, setNavOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();

  // Generate breadcrumbs based on current path
  const breadcrumbs = useMemo((): BreadcrumbItem[] => {
    const paths = location.pathname.split('/').filter(Boolean);
    const items: BreadcrumbItem[] = [];

    let currentPath = '';
    paths.forEach((path, index) => {
      currentPath += `/${path}`;

      // Capitalize and format the label
      const label =
        path.charAt(0).toUpperCase() + path.slice(1).replace(/-/g, ' ');

      items.push({
        label,
        href: index < paths.length - 1 ? currentPath : undefined,
      });
    });

    return items;
  }, [location.pathname]);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/admin/login');
  }, [logout, navigate]);

  const handleProfileClick = useCallback(() => {
    navigate('/admin/profile');
  }, [navigate]);

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header} role="banner">
        <div className={styles.headerContent}>
          {/* Left section - Menu button */}
          <div className={styles.headerLeft}>
            <DecorativeMenuButton
              onClick={() => setNavOpen(!navOpen)}
              isOpen={navOpen}
              variant={theme === 'dark' ? 'white' : 'default'}
              aria-label={
                navOpen ? 'Close navigation menu' : 'Open navigation menu'
              }
            />
          </div>

          {/* Center section - Logo */}
          <div className={styles.headerCenter}>
            <Logo
              src="/logo.svg"
              variant={theme === 'dark' ? 'white' : 'default'}
            />
          </div>

          {/* Right section - Theme Toggle, Avatar and Logout */}
          <div className={styles.headerRight}>
            <ThemeToggle theme={theme} onToggle={toggleTheme} />
            <Avatar
              name={user?.name || 'Admin User'}
              size="md"
              status="online"
              glass
              onClick={handleProfileClick}
            />
            <button
              onClick={handleLogout}
              className={styles.logoutButton}
              aria-label="Logout"
              title="Logout"
            >
              <Icon name="log-out" size="md" />
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <SlideNavigation
        items={navigationItems}
        isOpen={navOpen}
        onClose={() => setNavOpen(false)}
        currentPath={location.pathname}
      />

      {/* Main content */}
      <main className={styles.mainContent} role="main" id="main-content">
        {/* Breadcrumb */}
        {breadcrumbs.length > 0 && (
          <div className={styles.breadcrumbContainer}>
            <AdminBreadcrumb items={breadcrumbs} />
          </div>
        )}

        {/* Page content */}
        <div className={styles.pageContent}>{children}</div>
      </main>
    </div>
  );
};
